import React, { useEffect } from 'react';

import { useSnackbar } from 'notistack';
import { Helmet } from 'react-helmet';

import { Alert, Box, Checkbox, Container, FormControlLabel, Stack, Typography } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';

import { usePromoLoyaltyPromoKeyOptions } from '~/hooks/usePromoLoyaltyPromoKeyOptions';

import CopyableField from '../Common/CopyableField';
import PageSubheader from '../Common/Elements/PageSubheader';
import Spinner from '../Common/Spinner';
import DebugModal from '../DebugModal/DebugModal';
import usePromoAdminPermission from '../Promo/hooks/usePromoPermission';

const sharedOpts: Partial<GridColDef> = {
  editable: false,
  sortable: true,
  filterable: false,
  hideable: false,
  disableColumnMenu: true,
  flex: 1,
};

const getColumns = (loadCustomRules: boolean, isLoading: boolean): Array<GridColDef> => [
  {
    field: 'promoKey',
    headerName: 'Promo Key',
    ...sharedOpts,
    flex: 0.8,
    renderCell: (params) => {
      return <CopyableField value={params.row.promoKey} />;
    },
  },
  {
    field: 'bonus',
    headerName: 'Bonus',
    ...sharedOpts,
    flex: 0.8,
    renderCell: (params) => {
      if (!params.row.loyaltyCustomRule) {
        if (loadCustomRules && !isLoading) {
          return (
            <Alert severity="warning">
              Custom Rule could not be loaded from the loyalty service - Without the rule in IBS, order accrual
              calculations that use this rule WILL FAIL. Confirm this rule exists in the loyalty service and IBS
            </Alert>
          );
        }
        return 'not-loaded';
      }
      if (params.row.loyaltyCustomRule.type === 'BONUS_POINTS') {
        return <Typography variant="body2">{params.row.loyaltyCustomRule.pointsValue} POINTS</Typography>;
      } else if (params.row.loyaltyCustomRule.type === 'MULTIPLE_ON_BASE_POINTS') {
        return <Typography variant="body2">{params.row.loyaltyCustomRule.pointsMultiplier}x</Typography>;
      }
      return '-';
    },
  },
  {
    field: 'debug',
    headerName: 'Debug',
    ...sharedOpts,
    flex: 0.5,
    renderCell: (params) => {
      return <DebugModal type="generic" data={params.row} title="Loyalty Promo Key Option" />;
    },
  },
];

export default function LoyaltyPromoKeyOptionsContainer() {
  const { enqueueSnackbar } = useSnackbar();
  const isPromoAdmin = usePromoAdminPermission();

  const { form, total, data, isLoading, error, refetch } = usePromoLoyaltyPromoKeyOptions();

  useEffect(() => {
    if (error) {
      enqueueSnackbar(`Error loading loyalty promo key options: ${error.message}`, { variant: 'error' });
    }
  }, [error, enqueueSnackbar]);

  return (
    <Container maxWidth="xl">
      <Helmet>
        <title>Promo Loyalty Reward Options</title>
      </Helmet>
      <PageSubheader title="Promo Loyalty Reward Options" />
      <Typography variant="caption" color="text.secondary">
        "Promo Loyalty Reward Option" define how the loyalty reward is calculated. They are assigned to a promo code
        discount row and are applied (during checkout and loyalty accrual) to the order if the requirements for the
        discount row has been met.
      </Typography>
      <Box mt={2} mb={2}>
        <Stack direction="row" spacing={2}>
          <FormControlLabel
            control={
              <Checkbox
                id="lookupLoyaltyCustomRules"
                name="lookupLoyaltyCustomRules"
                checked={form.watch('lookupLoyaltyCustomRules')}
                onChange={(e) => form.setValue('lookupLoyaltyCustomRules', e.target.checked)}
              />
            }
            label="Lookup Loyalty Custom Rules"
          />
        </Stack>
      </Box>
      {!data ? (
        <Spinner />
      ) : (
        <Box mt={2}>
          <DataGrid
            loading={isLoading}
            rows={data.map((row, index) => ({ ...row, id: row.customRuleId || `row-${index}` }))}
            columns={getColumns(form.watch('lookupLoyaltyCustomRules'), isLoading)}
            autoHeight
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{
              pagination: {
                paginationModel: { page: 0, pageSize: 25 },
              },
            }}
            rowCount={total}
          />
        </Box>
      )}
    </Container>
  );
}
